import React from "react";
import { Link } from "react-router-dom";
import { useI18n } from "../i18n";
import { useSettings } from "../contexts/SettingsContext";
import { Store, ShieldCheck, TrendingUp, Truck, ArrowRight, CheckCircle2 } from "lucide-react";

const Sell = () => {
  const { lang } = useI18n();
  const { settings } = useSettings();
  const commission = settings?.marketplace?.default_commission_pct ?? 15;
  const policySummary = (lang === "nl" ? settings?.marketplace?.policy_summary_nl : settings?.marketplace?.policy_summary_en) || "";

  const c = lang === "nl" ? {
    eyebrow: "VERKOPER WORDEN",
    h1: "Verkoop op SnapShop",
    sub: "Bereik Nederlandse kopers die premium kwaliteit zoeken. Zorgvuldig gecureerd, eerlijke commissie, snelle uitbetaling.",
    cta: "Start aanvraag",
    secondary: "Marketplace beleid",
    why_t: "Waarom verkopen op SnapShop",
    benefits: [
      { t: "Gecureerd publiek", d: "Onze klanten verwachten kwaliteit. We tonen alleen producten die passen bij het SnapShop niveau." },
      { t: `${commission}% commissie`, d: "Eerlijk en transparant. Geen kosten voor inschrijving of maandelijkse abonnementen." },
      { t: "Wij verzorgen iDEAL & support", d: "Mollie checkout, BTW-conforme facturen, en NL klantenservice — wij regelen het." },
      { t: "Eén verzendbeleid", d: "PostNL & DHL standaardtarieven. Geen onderhandeling per verkoper, geen verwarring voor kopers." },
    ],
    rules_t: "Marketplace regels",
    rules: [
      "Geen dropshipping. Je houdt eigen voorraad of hebt aantoonbare leveranciers.",
      "Verifieerbaar Nederlands KvK + BTW nummer vereist.",
      "Eenvormig retourbeleid (30 dagen) voor alle verkopers.",
      "Producten die het beleid schenden worden door admin verwijderd.",
    ],
    flow_t: "Hoe werkt het",
    steps: [
      { n: "01", t: "Aanvraag indienen", d: "Vul bedrijfsgegevens in (KvK, BTW, IBAN). Duurt ~5 minuten." },
      { n: "02", t: "Wij beoordelen", d: "Admin verifieert je gegevens binnen 1-2 werkdagen." },
      { n: "03", t: "Begin met verkopen", d: "Na goedkeuring krijg je toegang tot je verkopersdashboard." },
    ],
    apply: "Aanvraag indienen",
    terms_link: "Lees de Algemene Voorwaarden voor Verkopers",
  } : {
    eyebrow: "BECOME A SELLER",
    h1: "Sell on SnapShop",
    sub: "Reach Dutch shoppers who expect premium quality. Carefully curated, fair commission, fast payout.",
    cta: "Start application",
    secondary: "Marketplace policy",
    why_t: "Why sell on SnapShop",
    benefits: [
      { t: "Curated audience", d: "Our customers expect quality. We only feature products that match the SnapShop bar." },
      { t: `${commission}% commission`, d: "Fair and transparent. No signup fees, no monthly subscription." },
      { t: "We handle iDEAL & support", d: "Mollie checkout, BTW-compliant invoices, and NL customer support — we run the rails." },
      { t: "Unified shipping", d: "Standard PostNL & DHL rates. No per-seller negotiation, no buyer confusion." },
    ],
    rules_t: "Marketplace rules",
    rules: [
      "No dropshipping. You hold your own stock or have verifiable suppliers.",
      "Verifiable Dutch KvK + BTW (VAT) number required.",
      "Unified 30-day return policy across all sellers.",
      "Products that violate the policy will be removed by admin.",
    ],
    flow_t: "How it works",
    steps: [
      { n: "01", t: "Submit application", d: "Fill in your company details (KvK, BTW, IBAN). Takes ~5 minutes." },
      { n: "02", t: "We review", d: "Admin verifies your details within 1-2 business days." },
      { n: "03", t: "Start selling", d: "Once approved, you get access to your seller dashboard." },
    ],
    apply: "Submit application",
    terms_link: "Read the Seller Terms & Conditions",
  };

  return (
    <div data-testid="sell-landing-page" className="bg-bone">
      {/* Hero */}
      <section className="max-w-[1400px] mx-auto px-6 lg:px-12 pt-16 pb-12 grid lg:grid-cols-12 gap-10 items-end">
        <div className="lg:col-span-7">
          <div className="font-label text-terracotta mb-3">{c.eyebrow}</div>
          <h1 className="font-display text-5xl lg:text-7xl tracking-tighter font-light leading-[0.95]">{c.h1}</h1>
          <p className="mt-6 text-ink-muted max-w-xl leading-relaxed">{c.sub}</p>
          <div className="mt-8 flex flex-wrap gap-3">
            <Link to="/sell/apply" className="btn-primary" data-testid="sell-cta-apply">
              {c.cta} <ArrowRight className="w-4 h-4" />
            </Link>
            <Link to="/legal/marketplace-policy" className="btn-secondary" data-testid="sell-cta-policy">
              {c.secondary}
            </Link>
          </div>
        </div>
        <div className="lg:col-span-5">
          <div className="border border-black/10 bg-white p-8 grid grid-cols-3 gap-6 text-center">
            <Stat n={`${commission}%`} l={lang === "nl" ? "Commissie" : "Commission"} />
            <Stat n="€0" l={lang === "nl" ? "Maandkosten" : "Monthly fee"} />
            <Stat n="1-2d" l={lang === "nl" ? "Goedkeuring" : "Approval"} />
          </div>
          {policySummary && (
            <div className="mt-4 text-xs text-ink-muted leading-relaxed border-l-2 border-terracotta pl-3">{policySummary}</div>
          )}
        </div>
      </section>

      {/* Benefits */}
      <section className="max-w-[1400px] mx-auto px-6 lg:px-12 py-14 border-t border-black/10">
        <h2 className="font-display text-3xl tracking-tight mb-10">{c.why_t}</h2>
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
          {c.benefits.map((b, i) => (
            <div key={i} className="border border-black/10 bg-white p-6">
              <div className="w-10 h-10 bg-secondary-warm flex items-center justify-center mb-4">
                {i === 0 && <Store className="w-5 h-5 text-terracotta" />}
                {i === 1 && <TrendingUp className="w-5 h-5 text-terracotta" />}
                {i === 2 && <ShieldCheck className="w-5 h-5 text-terracotta" />}
                {i === 3 && <Truck className="w-5 h-5 text-terracotta" />}
              </div>
              <div className="font-display text-lg">{b.t}</div>
              <p className="text-sm text-ink-muted mt-2 leading-relaxed">{b.d}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Rules */}
      <section className="max-w-[1400px] mx-auto px-6 lg:px-12 py-14 border-t border-black/10 grid lg:grid-cols-2 gap-10">
        <div>
          <h2 className="font-display text-3xl tracking-tight mb-6">{c.rules_t}</h2>
          <ul className="space-y-3">
            {c.rules.map((r, i) => (
              <li key={i} className="flex gap-3 text-sm">
                <CheckCircle2 className="w-5 h-5 text-olive shrink-0 mt-0.5" />
                <span>{r}</span>
              </li>
            ))}
          </ul>
          <Link to="/legal/seller-terms" className="inline-block mt-6 text-sm underline text-ink hover:text-terracotta" data-testid="sell-terms-link">
            {c.terms_link} →
          </Link>
        </div>
        <div>
          <h2 className="font-display text-3xl tracking-tight mb-6">{c.flow_t}</h2>
          <ol className="space-y-5">
            {c.steps.map((s) => (
              <li key={s.n} className="flex gap-5">
                <span className="font-display text-3xl text-terracotta tracking-tighter">{s.n}</span>
                <div>
                  <div className="font-display">{s.t}</div>
                  <div className="text-sm text-ink-muted">{s.d}</div>
                </div>
              </li>
            ))}
          </ol>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-ink text-white py-16">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12 text-center">
          <h2 className="font-display text-4xl lg:text-5xl tracking-tighter font-light">{lang === "nl" ? "Klaar om te beginnen?" : "Ready to begin?"}</h2>
          <Link to="/sell/apply" className="inline-flex mt-6 px-8 py-3 bg-terracotta hover:bg-[#b85f47] text-white font-label transition-colors" data-testid="sell-cta-apply-2">
            {c.apply}
          </Link>
        </div>
      </section>
    </div>
  );
};

const Stat = ({ n, l }) => (
  <div>
    <div className="font-display text-3xl tracking-tighter">{n}</div>
    <div className="font-label text-[10px] text-ink-muted mt-1">{l}</div>
  </div>
);

export default Sell;
